'use client';

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { CandlestickChart, type Candle } from '../CandlestickChart';
import { useChartType } from '../ChartTypeContext';

const RANGES = ['1D', '5D', '1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', '10Y'] as const;
const RANGE_MAP: Record<string, string> = {
  '1D': '1d', '5D': '5d', '1M': '1mo', '3M': '3mo',
  '6M': '6mo', '1Y': '1y', '2Y': '2y', '3Y': '3y',
  '5Y': '5y', '10Y': '10y',
};

interface PriceChartProps {
  symbol: string;
  height?: number;
  defaultRange?: string;
}

interface TerminalChartPoint {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

type ChartMode = 'line' | 'candle';

export function PriceChart({ symbol, height = 300, defaultRange = '1M' }: PriceChartProps) {
  const [range, setRange] = useState(defaultRange);
  const { chartType } = useChartType();
  // null = follow persisted preference; once the user clicks the toggle in this
  // session, that choice wins until the page is reloaded.
  const [override, setOverride] = useState<ChartMode | null>(null);
  const mode: ChartMode = override ?? chartType;
  const setMode = (m: ChartMode) => setOverride(m);
  const apiRange = RANGE_MAP[range] || '1mo';

  const { data: chartData, isLoading } = useQuery<TerminalChartPoint[]>({
    queryKey: ['/api/terminal/chart', symbol, apiRange],
    queryFn: async () => {
      const res = await fetch(`/api/terminal/chart/${encodeURIComponent(symbol)}?range=${apiRange}`);
      if (!res.ok) throw new Error('Failed to fetch chart');
      return res.json();
    },
    refetchInterval: range === '1D' ? 30000 : 300000,
  });

  const formatted = (chartData || []).map((d) => ({
    ...d,
    time: new Date(d.timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(range === '1D' || range === '5D' ? { hour: 'numeric', minute: '2-digit' } : {}),
    }),
  }));

  const prices = formatted.map((d) => d.close);
  const minPrice = prices.length ? Math.min(...prices) * 0.998 : 0;
  const maxPrice = prices.length ? Math.max(...prices) * 1.002 : 100;
  const isPositive = prices.length >= 2 ? prices[prices.length - 1] >= prices[0] : true;
  const color = isPositive ? '#00C853' : '#FF1744';

  const candleData = useMemo<Candle[]>(() => {
    return (chartData || [])
      .filter(
        (d) =>
          Number.isFinite(d.open) &&
          Number.isFinite(d.high) &&
          Number.isFinite(d.low) &&
          Number.isFinite(d.close),
      )
      .map((d) => ({
        time: d.timestamp * 1000,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
        volume: d.volume,
      }));
  }, [chartData]);

  const hasCandleData = candleData.length >= 2;
  // Derive what's actually rendered. If the user picked candle but the current
  // range has no OHLC, fall back to line for both the chart AND the toggle UI
  // so the active button reflects what's on screen.
  const effectiveMode: ChartMode = mode === 'candle' && hasCandleData ? 'candle' : 'line';

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-2 py-0.5 text-xs font-mono transition-colors ${
                range === r
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'text-[#6B7280] hover:text-[#E5E7EB] hover:bg-[#1F2937]'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {(['line', 'candle'] as const).map((m) => {
            const disabled = m === 'candle' && !hasCandleData;
            return (
              <button
                key={m}
                type="button"
                disabled={disabled}
                onClick={() => setMode(m)}
                title={disabled ? 'Candle data unavailable' : undefined}
                className={`px-2 py-0.5 text-xs font-mono transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  effectiveMode === m
                    ? 'bg-[var(--color-accent)] text-white'
                    : 'text-[#6B7280] hover:text-[#E5E7EB] hover:bg-[#1F2937]'
                }`}
              >
                {m === 'line' ? 'LINE' : 'CANDLE'}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        <div className="w-full bg-[#1F2937] animate-pulse rounded" style={{ height }} />
      ) : effectiveMode === 'candle' ? (
        <CandlestickChart
          key={range}
          data={candleData}
          height={height}
        />
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={formatted} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
            <defs>
              <linearGradient id={`grad-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              interval="preserveStartEnd"
              minTickGap={50}
            />
            <YAxis
              domain={[minPrice, maxPrice]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              tickFormatter={(v) => v.toFixed(2)}
              width={60}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1A2035',
                border: '1px solid #1F2937',
                borderRadius: '2px',
                fontSize: '12px',
                fontFamily: 'JetBrains Mono',
                color: '#E5E7EB',
              }}
              labelStyle={{ color: '#9CA3AF' }}
              formatter={(value: number | undefined) => [value?.toFixed(2) ?? '', 'Price']}
            />
            <Area
              type="monotone"
              dataKey="close"
              stroke={color}
              strokeWidth={1.5}
              fill={`url(#grad-${symbol})`}
              dot={false}
              activeDot={{ r: 3, fill: color }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
